'use client';

import * as React from 'react';
import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { cn } from 'utils/cn';
import { useOverlayScope } from 'components/ui/overlay-motion';

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
    return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ className, ...props }: PopoverPrimitive.Trigger.Props) {
    return (
        <PopoverPrimitive.Trigger data-slot="popover-trigger" className={cn('cursor-pointer', className)} {...props} />
    );
}

function PopoverContent({
    className,
    positionerClassName,
    backdropClassName,
    anchor,
    align = 'center',
    alignOffset = 0,
    side = 'bottom',
    sideOffset = 4,
    collisionAvoidance,
    collisionPadding,
    sticky,
    ...props
}: PopoverPrimitive.Popup.Props &
    Pick<
        PopoverPrimitive.Positioner.Props,
        | 'anchor'
        | 'align'
        | 'alignOffset'
        | 'side'
        | 'sideOffset'
        | 'collisionAvoidance'
        | 'collisionPadding'
        | 'sticky'
    > & {
        /** Classes for the portalled positioner — the place to raise `z-index` above app chrome. */
        positionerClassName?: string;
        /**
         * Renders a backdrop under the popup with these classes. A `modal` popover's own backdrop has no
         * `z-index`, so page content stacked above `auto` still takes the click; this one can be raised.
         */
        backdropClassName?: string;
    }) {
    const { container } = useOverlayScope();
    return (
        <PopoverPrimitive.Portal container={container}>
            {backdropClassName ? <PopoverPrimitive.Backdrop className={backdropClassName} /> : null}
            <PopoverPrimitive.Positioner
                anchor={anchor}
                align={align}
                alignOffset={alignOffset}
                side={side}
                sideOffset={sideOffset}
                collisionAvoidance={collisionAvoidance}
                collisionPadding={collisionPadding}
                sticky={sticky}
                className={cn('isolate z-(--z-popover)', positionerClassName)}
            >
                <PopoverPrimitive.Popup
                    data-slot="popover-content"
                    className={cn(
                        'bg-popover text-popover-foreground ring-foreground/10 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 new:ring-border-menu new:shadow-popover z-(--z-popover) flex w-72 origin-(--transform-origin) flex-col gap-4 rounded-md p-4 text-sm shadow-md ring-1 outline-hidden duration-100 motion-reduce:animate-none!',
                        className
                    )}
                    {...props}
                />
            </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
    );
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="popover-header" className={cn('flex flex-col gap-1 text-sm', className)} {...props} />;
}

function PopoverTitle({ className, ...props }: PopoverPrimitive.Title.Props) {
    return <PopoverPrimitive.Title data-slot="popover-title" className={cn('font-medium', className)} {...props} />;
}

function PopoverDescription({ className, ...props }: PopoverPrimitive.Description.Props) {
    return (
        <PopoverPrimitive.Description
            data-slot="popover-description"
            className={cn('text-muted-foreground', className)}
            {...props}
        />
    );
}

export { Popover, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger };
